import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Mail, MapPin } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("Focal Point");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password, location);
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const getErrorMessage = () => {
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(", ");
        return "Login failed";
      };
      setError(getErrorMessage());
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#122B63] relative overflow-hidden px-4" data-testid="login-screen">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)",
        }} />
      </div>

      <Card className="w-full max-w-md p-6 sm:p-8 relative z-10 shadow-2xl" data-testid="login-card">
        {/* Branding */}
        <div className="text-center mb-6 sm:mb-8">
          <img src="/logo.png" alt="STURDFIX Fasteners & Fixings" className="h-16 sm:h-20 mx-auto" />
          <h1
            className="text-sm sm:text-lg font-black tracking-wider text-[#1B3C87] uppercase mt-3"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="company-name"
          >
            SINGHANIA INTERNATIONAL LTD.
          </h1>
          <div className="mt-2 inline-block px-3 py-1 rounded bg-[#F4F5F7] text-xs text-gray-500 tracking-wide">
            Gate Management System
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Email</Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 sm:h-12 pl-10 text-sm"
                placeholder="Enter your email"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Password</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 sm:h-12 pl-10 text-sm"
                placeholder="Enter your password"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 flex items-center gap-1">
              <MapPin size={12} /> Gate Location
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {["Focal Point", "Sahnewal"].map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setLocation(loc)}
                  data-testid={`location-btn-${loc.toLowerCase().replace(" ", "-")}`}
                  className={`py-2.5 sm:py-3 rounded text-sm font-semibold border-2 transition-all ${
                    location === loc
                      ? "bg-[#1B3C87] text-white border-[#1B3C87]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#1B3C87]/40"
                  }`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 sm:h-12 bg-[#1B3C87] hover:bg-[#2650A8] text-sm font-semibold tracking-wide"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t text-center text-[10px] text-gray-400 tracking-wider uppercase">
          ISO 9001:2015 · IATF 16949 · CTPAT Compliant
        </div>
      </Card>
    </div>
  );
}
